import React from "react";

/**
 * CategoryTabs — shared, pixel-identical tab strip used by /prompts and the
 * homepage directory. Each tab is either:
 *  { id, label }                — a plain tab
 *  { id, label, prefix: "★" }   — a starred tab (for special categories)
 *
 * Visuals (as per the /prompts reference):
 *  • Subtle full-width horizontal divider line ABOVE the tab row.
 *  • Active tab       → solid black pill (bg-#0B1220, white text, rounded-full).
 *  • Inactive tab     → PLAIN TEXT — no border, no background, hover darkens.
 *  • Generous horizontal spacing between tabs.
 *  • Left-aligned to the container's content edge (no centering, no bordered box).
 *
 * The component is intentionally uncontrolled about styling — callers pass in
 * the tab list, the active `value`, and an `onChange` handler.
 */
export function CategoryTabs({
  tabs,
  value,
  onChange,
  testIdPrefix = "cat-tab",
  className = "",
}) {
  return (
    <div className={`border-t border-[#E7EAF0] pt-4 mb-6 ${className}`} data-testid="category-tabs">
      <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide pb-1">
        {tabs.map((t) => {
          const active = t.id === value;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              data-testid={`${testIdPrefix}-${String(t.id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={
                active
                  ? "px-4 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap bg-[#0B1220] text-white transition-colors"
                  : "px-1 py-1.5 text-[13px] font-medium whitespace-nowrap bg-transparent border-0 text-[#5B6478] hover:text-[#0B1220] transition-colors"
              }
            >
              {t.prefix ? `${t.prefix} ${t.label}` : t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
